<template>
    <div id="page-sign-up" data-page="sign-up" class="auth-page-container d-flex align-items-center justify-content-center min-vh-100 py-4 py-md-5 px-3">
        <div class="auth-card p-4 px-md-5 py-md-4 rounded shadow-lg text-center" v-if="!loadingToken">
            <div class="mb-3 text-center">
                <div class="mb-1">
                    <router-link to="/" class="d-inline-flex align-items-center text-decoration-none">
                        <img :src="logoUrl" alt="VelocyTrack Logo" class="auth-logo mr-2" />
                        <h3 class="font-weight-bold text-white mb-0" style="font-size: 1.6rem; letter-spacing: -0.02em;">Complete Sign Up</h3>
                    </router-link>
                </div>
                <p class="text-muted small mb-0">Welcome! Set a password or connect your Google SSO to activate your Beta account.</p>
            </div>

            <b-alert variant="danger" :show="!!errorMessage" class="text-left small mb-4" data-test="alert-signup-error">
                {{ errorMessage }}
            </b-alert>

            <ValidationObserver ref="observer" v-slot="{ handleSubmit }" v-if="isValidToken">
                <b-row class="text-left align-items-stretch">
                    <!-- Direct credentials column -->
                    <b-col cols="12" md="6" class="pr-md-4 mb-4 mb-md-0 auth-form-col">
                        <b-form @submit.prevent="handleSubmit(onSignUp)" class="h-100 d-flex flex-column justify-content-between">
                            <div>
                                <b-form-group label="Email Address" label-class="text-light small font-weight-bold" class="mb-3">
                                    <b-form-input :value="account.email"
                                                  id="signup-email"
                                                  name="username"
                                                  autocomplete="username"
                                                  type="email"
                                                  readonly
                                                  data-test="input-signup-email"
                                                  class="auth-input disabled-input">
                                    </b-form-input>
                                </b-form-group>

                                <b-form-group label="Full Name" label-class="text-light small font-weight-bold" class="mb-3">
                                    <b-form-input v-model="account.fullName"
                                                  id="signup-fullname"
                                                  name="name"
                                                  autocomplete="name"
                                                  type="text"
                                                  placeholder="Your full name"
                                                  data-test="input-signup-fullname"
                                                  class="auth-input">
                                    </b-form-input>
                                </b-form-group>

                                <ValidationProvider rules="required|min:6" name="Password" vid="password" v-slot="{ errors }">
                                    <b-form-group label="Choose a Password" label-class="text-light small font-weight-bold" class="mb-3">
                                        <b-form-input v-model="form.password"
                                                      id="password"
                                                      name="password"
                                                      autocomplete="new-password"
                                                      type="password"
                                                      placeholder="Must be at least 6 characters"
                                                      :state="errors.length > 0 ? false : null"
                                                      data-test="input-signup-password"
                                                      class="auth-input">
                                        </b-form-input>
                                        <b-form-invalid-feedback>{{ errors[0] }}</b-form-invalid-feedback>
                                    </b-form-group>
                                </ValidationProvider>

                                <ValidationProvider rules="required|confirmed:password" name="Confirm Password" v-slot="{ errors }">
                                    <b-form-group label="Confirm Password" label-class="text-light small font-weight-bold" class="mb-3">
                                        <b-form-input v-model="form.confirmPassword"
                                                      id="confirm-password"
                                                      name="confirm-password"
                                                      autocomplete="new-password"
                                                      type="password"
                                                      placeholder="Re-enter your password"
                                                      :state="errors.length > 0 ? false : null"
                                                      data-test="input-signup-confirm-password"
                                                      class="auth-input">
                                        </b-form-input>
                                        <b-form-invalid-feedback>{{ errors[0] }}</b-form-invalid-feedback>
                                    </b-form-group>
                                </ValidationProvider>

                                <div ref="turnstileContainer" class="mb-3 d-flex justify-content-center"></div>
                            </div>

                            <b-button type="submit" variant="primary" block class="btn-aceternity-primary font-weight-bold py-2 mt-2" :disabled="loading || !turnstileToken" data-test="btn-submit-signup" :data-state="loading ? 'loading' : 'idle'">
                                <b-spinner small v-if="loading" class="mr-2"></b-spinner>
                                Complete Sign Up
                            </b-button>
                        </b-form>
                    </b-col>

                    <!-- Single Sign-On column -->
                    <b-col cols="12" md="6" class="pl-md-4 auth-sso-col d-flex flex-column justify-content-between">
                        <div>
                            <p class="text-muted small mb-3 text-center text-md-left">Or activate using SSO:</p>
                            <div class="sso-buttons-stack d-flex flex-column align-items-center align-items-md-start w-100">
                                <!-- Google SSO Item -->
                                <div class="w-100 mb-3">
                                    <google-sign-in :clientId="googleClientId" :width="320" theme="filled_black" text="continue_with" :rememberedAccount="lastGoogleAccount" :emitTokenOnly="true" :suppressToast="true" @id-token="onGoogleIdToken" @error="onGoogleError" class="w-100" data-test="btn-signup-google"></google-sign-in>
                                </div>

                                <!-- GitHub SSO Item -->
                                <div class="w-100 mb-3">
                                    <b-button variant="outline-light" class="w-100 d-flex align-items-center justify-content-between sso-btn px-3" data-test="btn-signup-github" aria-label="Sign up with GitHub" title="Sign up with GitHub" @click="loginOAuth('GitHub')">
                                        <div class="d-flex align-items-center">
                                            <i class="bi bi-github mr-2 font-size-18"></i>
                                            <span>Continue with GitHub</span>
                                        </div>
                                        <div v-if="lastGithubAccount" class="d-flex align-items-center ml-2" data-test="avatar-signup-github-account" :title="'Last used: ' + (lastGithubAccount.name || lastGithubAccount.email)">
                                            <b-avatar :src="lastGithubAccount.picture" :text="getInitials(lastGithubAccount.name)" size="24px" class="border border-secondary"></b-avatar>
                                        </div>
                                    </b-button>
                                </div>
                            </div>
                            <div class="small text-muted text-left mb-4" style="line-height: 1.5; font-size: 0.85rem;">
                                <i class="bi bi-shield-check text-info mr-1"></i>
                                Signing up with your organization or developer account links your verified invitation and activates your Beta access.
                            </div>
                        </div>

                        <div class="text-left pt-3 border-top border-secondary">
                            <p class="text-muted small mb-0" style="font-size: 0.8rem; line-height: 1.45;">
                                <i class="bi bi-cpu text-info mr-1"></i>
                                <strong>Stateless On-The-Fly Proxy:</strong> Third-party API data is processed transiently in volatile memory (RAM) and never cloned or stored at rest.
                            </p>
                        </div>
                    </b-col>
                </b-row>
            </ValidationObserver>

            <div v-else>
                <b-button variant="outline-light" class="mt-3" to="/sign-in" data-test="btn-goto-signin">Go to Sign In</b-button>
            </div>
        </div>
        <div v-else class="text-center" data-state="loading" aria-busy="true">
            <load-spinner variant="light" size="lg" text="Loading..." />
        </div>
    </div>
</template>

<script>
    import GoogleSignIn from './google-sign-in.vue';
    import { ValidationObserver, ValidationProvider } from 'vee-validate';
    import accountMixin from '../mixinAccount';

    export default {
        name: 'page-sign-up',
        mixins: [accountMixin],
        components: {
            GoogleSignIn,
            ValidationObserver,
            ValidationProvider
        },
        data() {
            return {
                token: this.$route.params.token,
                account: {
                    email: '',
                    fullName: ''
                },
                form: {
                    password: '',
                    confirmPassword: ''
                },
                isValidToken: false,
                loadingToken: true,
                errorMessage: '',
                loading: false,
                turnstileToken: null,
                turnstileWidgetId: null,
                lastGoogleAccount: null,
                lastGithubAccount: null
            };
        },
        computed: {
            googleClientId() {
                return process.env.VUE_APP_GOOGLE_CLIENT_ID;
            }
        },
        mounted() {
            document.documentElement.setAttribute('data-theme', 'dark');
            this.loadRememberedAccounts();
            if (this.isAuthenticated) {
                this.$router.replace('/timelog').catch(() => {});
                return;
            }
            if (this.$route.query.error) {
                this.errorMessage = this.$route.query.error;
            }
            if (this.token) {
                try {
                    localStorage.setItem('velocytrack_invite_token', this.token);
                } catch (e) {
                    // Ignore
                }
            }
            this.validateToken();
            this.initTurnstile();
        },
        beforeUnmount() {
            if (window.turnstile && this.turnstileWidgetId) {
                try {
                    window.turnstile.remove(this.turnstileWidgetId);
                } catch (e) {
                    // Ignore cleanup error
                }
            }
        },
        methods: {
            async validateToken() {
                this.loadingToken = true;
                try {
                    const response = await this.$http.get(`/Account/ValidateInvitation?token=${this.token}`);
                    this.account = response.data;
                    this.isValidToken = true;
                } catch (error) {
                    this.isValidToken = false;
                    this.errorMessage = error.response?.data?.message || error.response?.data || 'Invalid or expired invitation token.';
                } finally {
                    this.loadingToken = false;
                }
            },
            initTurnstile() {
                if (this.turnstileWidgetId !== null) return;
                if (!window.turnstile && !document.getElementById('cf-turnstile-script')) {
                    const s = document.createElement('script');
                    s.id = 'cf-turnstile-script';
                    s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
                    s.async = true;
                    s.defer = true;
                    document.head.appendChild(s);
                }
                const interval = setInterval(() => {
                    if (window.turnstile && this.$refs.turnstileContainer) {
                        clearInterval(interval);
                        if (this.turnstileWidgetId !== null) return;
                        try {
                            this.turnstileWidgetId = window.turnstile.render(this.$refs.turnstileContainer, {
                                sitekey: process.env.VUE_APP_TURNSTILE_SITE_KEY || '0x4AAAAAAFMGqfq985aa_XZc',
                                theme: 'dark',
                                action: 'signup',
                                callback: (token) => {
                                    this.turnstileToken = token;
                                },
                                'expired-callback': () => {
                                    this.turnstileToken = null;
                                },
                                'error-callback': () => {
                                    this.turnstileToken = null;
                                }
                            });
                        } catch (e) {
                            // Widget already rendered
                        }
                    }
                }, 200);
            },
            resetTurnstile() {
                if (window.turnstile && this.turnstileWidgetId) {
                    try {
                        window.turnstile.reset(this.turnstileWidgetId);
                    } catch (e) {
                        // Ignore reset error
                    }
                }
                this.turnstileToken = null;
            },
            getInitials(name) {
                if (!name) return 'U';
                const parts = name.trim().split(/\s+/);
                if (parts.length >= 2) {
                    return (parts[0][0] + parts[1][0]).toUpperCase();
                }
                return name.slice(0, 2).toUpperCase();
            },
            loadRememberedAccounts() {
                try {
                    const g = localStorage.getItem('velocytrack_last_google_account');
                    if (g) this.lastGoogleAccount = JSON.parse(g);
                } catch (_) {
                    // Ignore localStorage errors
                }
                try {
                    const gh = localStorage.getItem('velocytrack_last_github_account');
                    if (gh) this.lastGithubAccount = JSON.parse(gh);
                } catch (_) {
                    // Ignore localStorage errors
                }
            },
            async onSignUp() {
                this.errorMessage = '';
                this.loading = true;
                try {
                    const payload = {
                        token: this.token,
                        fullName: this.account.fullName,
                        password: this.form.password,
                        turnstileToken: this.turnstileToken
                    };
                    const response = await this.$http.post('/Account/CompleteSignUp', payload);
                    try {
                        localStorage.setItem('velocytrack_last_email_account', JSON.stringify({
                            email: response.data.email,
                            name: response.data.fullName,
                            picture: response.data.picture,
                            provider: 'Password',
                            lastLogin: new Date().toISOString()
                        }));
                    } catch (_) {
                        // Ignore localStorage errors
                    }
                    this.$store.commit('setAccount', response.data);
                    this.$store.commit('setAuthenticated', true);
                    await this.initSessionConfigAsync();
                    this.$router.push('/timelog').catch(() => {});
                } catch (error) {
                    this.errorMessage = error.response?.data?.message || (typeof error.response?.data === 'string' ? error.response.data : null) || 'Sign up failed. Please try again.';
                    this.resetTurnstile();
                } finally {
                    this.loading = false;
                }
            },
            async onGoogleIdToken(tokenData) {
                this.errorMessage = '';
                this.loading = true;
                try {
                    const idToken = typeof tokenData === 'string' ? tokenData : tokenData?.idToken;
                    const accessToken = typeof tokenData === 'object' ? tokenData?.accessToken : null;
                    const payload = {
                        token: this.token,
                        fullName: this.account.fullName,
                        idToken: idToken,
                        accessToken: accessToken,
                        turnstileToken: this.turnstileToken
                    };
                    const response = await this.$http.post('/Account/CompleteSignUpWithGoogle', payload);
                    try {
                        localStorage.setItem('velocytrack_last_google_account', JSON.stringify({
                            email: response.data.email,
                            name: response.data.fullName,
                            picture: response.data.picture,
                            provider: 'Google',
                            lastLogin: new Date().toISOString()
                        }));
                    } catch (_) {
                        // Ignore localStorage errors
                    }
                    this.$store.commit('setAccount', response.data);
                    this.$store.commit('setAuthenticated', true);
                    await this.initSessionConfigAsync();
                    this.$router.push('/timelog').catch(() => {});
                } catch (error) {
                    this.errorMessage = error.response?.data?.message || (typeof error.response?.data === 'string' ? error.response.data : null) || 'Google Sign Up failed. Please try again.';
                    this.resetTurnstile();
                } finally {
                    this.loading = false;
                }
            },
            onGoogleError(msg) {
                this.errorMessage = typeof msg === 'string' ? msg : (msg?.message || 'Google sign up failed. Please try again.');
            },
            loginOAuth(provider) {
                const returnUrl = encodeURIComponent(window.location.origin);
                const tokenParam = this.token ? `&token=${encodeURIComponent(this.token)}` : '';
                window.location.href = `${this.$http.defaults.baseURL}/Account/ExternalLogin?provider=${provider}${tokenParam}&returnUrl=${returnUrl}&prompt=select_account`;
            }
        }
    };
</script>

<style scoped>
    .auth-page-container {
        background-color: #030712;
        color: #f1f5f9;
        font-family: 'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
    }

    .auth-card {
        background-color: #0f172a;
        border: 1px solid rgba(255, 255, 255, 0.08);
        width: 100%;
        max-width: 760px;
    }

    .auth-logo {
        height: 32px;
        width: auto;
    }

    .auth-input {
        background-color: #1e293b;
        border-color: rgba(255, 255, 255, 0.15);
        color: #fff;
    }

    .auth-input:focus {
        background-color: #1e293b;
        border-color: #38bdf8;
        color: #fff;
        box-shadow: 0 0 0 0.2rem rgba(56, 189, 248, 0.25);
    }

    .disabled-input {
        opacity: 0.7;
        cursor: not-allowed;
    }

    .border-secondary {
        border-color: rgba(255,255,255,0.1) !important;
    }

    .btn-aceternity-primary {
        background: linear-gradient(135deg, #0284c7, #38bdf8);
        border: none;
        color: white;
        transition: transform 0.2s, box-shadow 0.2s;
    }

    .btn-aceternity-primary:hover:not(:disabled) {
        transform: translateY(-2px);
        box-shadow: 0 10px 20px -10px rgba(56, 189, 248, 0.5);
    }

    .sso-buttons-stack {
        width: 100%;
        max-width: 320px;
    }

    .sso-btn {
        background-color: #131314;
        border: 1px solid #3c4043;
        color: #e3e3e3;
        font-size: 14px;
        font-weight: 500;
        height: 40px;
        border-radius: 4px;
        box-sizing: border-box;
        transition: background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease;
    }

    .sso-btn:hover {
        background-color: #202124;
        border-color: #5f6368;
        color: #ffffff;
    }

    .sso-btn:active {
        background-color: #303134;
    }

    @media (min-width: 768px) {
        .auth-form-col {
            border-right: 1px solid rgba(255, 255, 255, 0.1);
        }
    }

    @media (max-width: 767.98px) {
        .auth-sso-col {
            border-top: 1px solid rgba(255, 255, 255, 0.1);
            padding-top: 1.5rem;
        }
    }
</style>
